import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "../components/Common/Button";
import ConfirmModal from "../components/Common/ConfirmModal";
import Input from "../components/Common/Input";
import Label from "../components/Common/Label";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import Pagination from "../components/Common/Pagination";
import ToastMessage from "../components/Common/ToastMessage";
import AllowedSerialNumbersPanel from "../components/CustomerManagement/AllowedSerialNumbersPanel";
import ApiKeysPanel from "../components/CustomerManagement/ApiKeysPanel";
import { fieldClassName } from "../components/CustomerManagement/formStyles";
import CustomerUsersPanel from "../components/CustomerManagement/CustomerUsersPanel";
import { isValidEmail } from "../utils/common_utils";
import {
  activateFMSCustomer,
  createFMSCustomer,
  deleteFMSCustomer,
  getFMSCustomer,
  getFMSCustomers,
  updateFMSCustomer,
} from "../lib/api";

const PAGE_SIZE = 10;
const INACTIVE_STATUS = "INACTIVE";

const emptyFormState = {
  name: "",
  primaryContact: "",
};

const formatDate = (value) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const formatUnitCounts = (customer) => {
  const numUnits = customer?.numUnits ?? 0;
  if (customer?.numLicensedUnits === null || customer?.numLicensedUnits === undefined) {
    return `${numUnits}`;
  }
  return `${numUnits} / ${customer.numLicensedUnits}`;
};

const statusBadgeStyles = {
  INACTIVE: "bg-[#FDE8E8] text-[#B42318] dark:bg-[#4A1F1C] dark:text-[#FFD5D0]",
  ON_TRIAL: "bg-[#FFF1C6] text-[#8A5A00] dark:bg-[#4A3A12] dark:text-[#FFE29B]",
  PAID: "bg-[#DDF5ED] text-[#0B8F6A] dark:bg-[#12403A] dark:text-[#9BE7CE]",
};

const statusBadgeClassName = (status) =>
  statusBadgeStyles[status] || "bg-[#F1F2F4] text-[#5F6368] dark:bg-white/10 dark:text-white/70";

const toFormState = (customer) => ({
  name: customer?.name || "",
  primaryContact: customer?.primaryContact || "",
});

const areFormsEqual = (left, right) =>
  left.name === right.name && left.primaryContact === right.primaryContact;

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClassName(status)}`}
    data-testid="customer-status-badge"
  >
    {status}
  </span>
);

const CustomerListView = ({
  currentPage,
  customers,
  isLoading,
  onActivate,
  onCreate,
  onDelete,
  onEdit,
  onPageChange,
  pageCount,
  totalCount,
}) => (
  <div className="flex flex-col gap-6">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]">
          Customer Management
        </h2>
        <p className="text-dark-grey dark:text-white/70 text-sm mt-2">
          Create and manage customer accounts, their users, and their allowed units.
        </p>
      </div>
      <Button
        className="flex items-center justify-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition w-full sm:w-auto"
        onClick={onCreate}
        data-testid="create-customer-button"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 5V19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M5 12H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        Create Customer
      </Button>
    </div>

    <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 shadow-appShadow overflow-hidden">
      <div className="border-b border-[#E6E8EC] dark:border-white/10 px-5 py-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-dark-grey dark:text-white">Customers</h3>
        <span className="text-sm text-dark-grey/70 dark:text-white/60">{totalCount} total</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-64">
          <LoadingSpinner />
        </div>
      ) : customers.length === 0 ? (
        <div className="px-5 py-16 text-center" data-testid="customers-empty">
          <p className="text-lg font-semibold text-dark-grey dark:text-white">No customers found</p>
          <p className="mt-2 text-sm text-dark-grey/70 dark:text-white/60">
            Create the first customer to get started.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#E6E8EC] dark:divide-white/10">
          {customers.map((customer) => (
            <div
              key={customer.unid}
              className="px-5 py-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
              data-testid={`customer-row-${customer.unid}`}
            >
              <Button
                className="text-left flex-1"
                onClick={() => onEdit(customer.unid)}
                data-testid={`customer-summary-${customer.unid}`}
              >
                <span className="flex flex-wrap items-center gap-3">
                  <span className="text-base font-semibold text-dark-grey dark:text-white">
                    {customer.name}
                  </span>
                  <StatusBadge status={customer.status} />
                </span>
                <span className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-dark-grey/70 dark:text-white/60">
                  <span className="break-all">{customer.primaryContact}</span>
                  <span>{formatUnitCounts(customer)} units</span>
                  <span>License expires {formatDate(customer.licenseExpiresAt)}</span>
                </span>
              </Button>

              <div className="flex flex-wrap items-center gap-3">
                {customer.status === INACTIVE_STATUS && (
                  <Button
                    className="px-4 py-2 rounded-md bg-[#0B8F6A] text-white text-sm font-semibold hover:bg-[#0B8F6A]/90 transition"
                    onClick={() => onActivate(customer)}
                    data-testid={`activate-customer-${customer.unid}`}
                  >
                    Activate
                  </Button>
                )}
                <Button
                  className="px-4 py-2 rounded-md border border-theme text-theme text-sm font-semibold hover:bg-theme hover:text-white transition"
                  onClick={() => onEdit(customer.unid)}
                  data-testid={`edit-customer-${customer.unid}`}
                >
                  Edit
                </Button>
                <Button
                  className="px-4 py-2 rounded-md border border-[#EF4444] text-[#EF4444] text-sm font-semibold hover:bg-[#EF4444] hover:text-white transition"
                  onClick={() => onDelete(customer)}
                  data-testid={`delete-customer-${customer.unid}`}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {pageCount > 1 && (
      <Pagination
        currentPage={currentPage}
        pageCount={pageCount}
        onPageChange={({ selected }) => onPageChange(selected)}
      />
    )}
  </div>
);

const CustomerEditorView = ({
  customer,
  formState,
  initialFormState,
  isActivating,
  isCreating,
  isDeleting,
  isLoading,
  isSaving,
  onActivate,
  onBack,
  onDelete,
  onNotify,
  onSave,
  setFormState,
}) => {
  const hasChanges = !areFormsEqual(formState, initialFormState);
  const isSaveDisabled =
    isLoading ||
    isSaving ||
    !formState.name.trim() ||
    !isValidEmail(formState.primaryContact) ||
    (!isCreating && !hasChanges);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Button
            className="inline-flex items-center gap-2 text-sm font-semibold text-theme hover:text-theme/80 transition"
            onClick={onBack}
            data-testid="back-to-customers-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Customers
          </Button>
          <h2 className="mt-4 text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]">
            {isCreating ? "Create Customer" : customer?.name || "Edit Customer"}
          </h2>
          <p className="text-dark-grey dark:text-white/70 text-sm mt-2">
            {isCreating
              ? "Set up a new customer account."
              : "Update customer details, users, and allowed units."}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          {!isCreating && customer?.status === INACTIVE_STATUS && (
            <Button
              className="px-4 py-2 rounded-md bg-[#0B8F6A] text-white text-sm font-semibold hover:bg-[#0B8F6A]/90 transition w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onActivate}
              disabled={isActivating}
              data-testid="activate-customer-button"
            >
              {isActivating ? "Activating..." : "Activate"}
            </Button>
          )}
          {!isCreating && (
            <Button
              className="px-4 py-2 rounded-md border border-[#EF4444] text-[#EF4444] text-sm font-semibold hover:bg-[#EF4444] hover:text-white transition w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onDelete}
              disabled={isDeleting}
              data-testid="delete-customer-button"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          )}
          <Button
            className="px-4 py-2 rounded-md bg-theme text-white text-sm font-semibold hover:bg-theme/90 transition w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onSave}
            disabled={isSaveDisabled}
            data-testid="save-customer-button"
          >
            {isSaving ? "Saving..." : isCreating ? "Create Customer" : "Save Customer"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-80">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {!isCreating && customer && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 p-5 shadow-appShadow">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-dark-grey/60 dark:text-white/60">
                  Status
                </p>
                <p className="mt-3">
                  <StatusBadge status={customer.status} />
                </p>
              </div>
              <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 p-5 shadow-appShadow">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-dark-grey/60 dark:text-white/60">
                  Units / Licensed
                </p>
                <p className="mt-3 text-sm font-semibold text-dark-grey dark:text-white">
                  {formatUnitCounts(customer)}
                </p>
              </div>
              <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 p-5 shadow-appShadow">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-dark-grey/60 dark:text-white/60">
                  License Expires
                </p>
                <p className="mt-3 text-sm font-semibold text-dark-grey dark:text-white">
                  {formatDate(customer.licenseExpiresAt)}
                </p>
              </div>
              <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 p-5 shadow-appShadow">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-dark-grey/60 dark:text-white/60">
                  Customer ID
                </p>
                <p className="mt-3 text-sm font-semibold text-dark-grey dark:text-white break-all">
                  {customer.unid}
                </p>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 shadow-appShadow p-5 sm:p-6">
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <Label
                    className="text-sm font-semibold text-dark-grey dark:text-white"
                    htmlFor="customer-name"
                  >
                    Name
                  </Label>
                  <Input
                    id="customer-name"
                    value={formState.name}
                    onChange={(event) => {
                      const { value } = event.target;
                      setFormState((current) => ({ ...current, name: value }));
                    }}
                    placeholder="Acme Property Group"
                    className={fieldClassName}
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label
                    className="text-sm font-semibold text-dark-grey dark:text-white"
                    htmlFor="customer-primary-contact"
                  >
                    Primary Contact
                  </Label>
                  <Input
                    id="customer-primary-contact"
                    type="email"
                    value={formState.primaryContact}
                    onChange={(event) => {
                      const { value } = event.target;
                      setFormState((current) => ({ ...current, primaryContact: value }));
                    }}
                    placeholder="contact@example.com"
                    className={fieldClassName}
                    data-testid="customer-primary-contact-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {!isCreating && customer && (
            <>
              <CustomerUsersPanel
                customerUnid={customer.unid}
                primaryContact={customer.primaryContact}
                onNotify={onNotify}
              />
              <AllowedSerialNumbersPanel customerUnid={customer.unid} onNotify={onNotify} />
              <ApiKeysPanel customerUnid={customer.unid} onNotify={onNotify} />
            </>
          )}
        </>
      )}
    </div>
  );
};

const CustomerManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  const isCreating = customerId === "new";
  const isEditing = Boolean(customerId) && customerId !== "new";
  const isListView = !customerId;

  const [customers, setCustomers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isListLoading, setIsListLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [formState, setFormState] = useState(emptyFormState);
  const [initialFormState, setInitialFormState] = useState(emptyFormState);
  const [customer, setCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [customerToActivate, setCustomerToActivate] = useState(null);
  const [isDiscardChangesOpen, setIsDiscardChangesOpen] = useState(false);
  const [toast, setToast] = useState({
    id: 0,
    show: false,
    message: "",
    type: "success",
  });

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);
  const hasUnsavedChanges = !areFormsEqual(formState, initialFormState);

  const showToast = useCallback((message, type = "success") => {
    setToast((current) => ({
      id: current.id + 1,
      show: true,
      message,
      type,
    }));
  }, []);

  const loadCustomers = useCallback(async () => {
    setIsListLoading(true);
    try {
      const response = await getFMSCustomers({
        pageNumber: currentPage + 1,
        pageSize: PAGE_SIZE,
      });
      const payload = response?.data?.data || {};
      setCustomers(payload.customers || []);
      setTotalCount(payload.count || 0);
    } catch (error) {
      setCustomers([]);
      setTotalCount(0);
      showToast(error.message || "Failed to fetch customers.", "failed");
    } finally {
      setIsListLoading(false);
    }
  }, [currentPage, showToast]);

  const loadCustomer = useCallback(async ({ background = false } = {}) => {
    if (!isEditing) return;

    if (!background) {
      setIsDetailLoading(true);
    }
    try {
      const response = await getFMSCustomer(customerId);
      const fetchedCustomer = response?.data?.data?.customer;

      if (!fetchedCustomer) {
        throw new Error("Customer not found.");
      }

      setCustomer(fetchedCustomer);

      if (!background) {
        const nextFormState = toFormState(fetchedCustomer);
        setFormState(nextFormState);
        setInitialFormState(nextFormState);
      }
    } catch (error) {
      const message = error.message || "Failed to fetch customer.";

      if (background) {
        showToast(message, "failed");
        return;
      }

      navigate("/customer-management", {
        replace: true,
        state: {
          toast: {
            message,
            type: "failed",
          },
        },
      });
    } finally {
      if (!background) {
        setIsDetailLoading(false);
      }
    }
  }, [customerId, isEditing, navigate, showToast]);

  useEffect(() => {
    const toastState = location.state?.toast;
    if (!toastState) return;

    showToast(toastState.message, toastState.type);
    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.state, navigate, showToast]);

  useEffect(() => {
    if (isListView) {
      loadCustomers();
    }
  }, [isListView, loadCustomers]);

  useEffect(() => {
    if (isCreating) {
      setFormState(emptyFormState);
      setInitialFormState(emptyFormState);
      setCustomer(null);
      setIsDetailLoading(false);
      return;
    }

    if (isEditing) {
      loadCustomer();
    }
  }, [isCreating, isEditing, loadCustomer]);

  useEffect(() => {
    if (!isListView) return;
    if (currentPage > 0 && pageCount > 0 && currentPage > pageCount - 1) {
      setCurrentPage(pageCount - 1);
    }
  }, [currentPage, isListView, pageCount]);

  const handleSave = async () => {
    const payload = {
      name: formState.name.trim(),
      primaryContact: formState.primaryContact.trim(),
    };

    if (!payload.name) {
      showToast("Name is required.", "failed");
      return;
    }

    if (!isValidEmail(payload.primaryContact)) {
      showToast("A valid primary contact email is required.", "failed");
      return;
    }

    setIsSaving(true);
    try {
      if (isCreating) {
        const response = await createFMSCustomer(payload);
        const createdCustomer = response?.data?.data?.customer;

        if (!createdCustomer?.unid) {
          throw new Error("Customer was created but no id was returned.");
        }

        navigate(`/customer-management/${createdCustomer.unid}`, {
          replace: true,
          state: {
            toast: {
              message: "Customer created successfully.",
              type: "success",
            },
          },
        });
        return;
      }

      const response = await updateFMSCustomer(customerId, payload);
      const updatedCustomer = response?.data?.data?.customer;
      const nextFormState = toFormState(updatedCustomer || payload);

      setFormState(nextFormState);
      setInitialFormState(nextFormState);
      setCustomer(updatedCustomer || customer);
      showToast("Customer updated successfully.", "success");
    } catch (error) {
      showToast(error.message || "Failed to save customer.", "failed");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!customerToDelete?.unid) return;

    setIsDeleting(true);
    try {
      await deleteFMSCustomer(customerToDelete.unid);
      setCustomerToDelete(null);

      if (isEditing && customerToDelete.unid === customerId) {
        navigate("/customer-management", {
          replace: true,
          state: {
            toast: {
              message: "Customer deleted successfully.",
              type: "success",
            },
          },
        });
        return;
      }

      showToast("Customer deleted successfully.", "success");

      const nextPageCount = Math.ceil(Math.max(totalCount - 1, 0) / PAGE_SIZE);
      if (currentPage > 0 && currentPage >= nextPageCount) {
        setCurrentPage(Math.max(nextPageCount - 1, 0));
      } else {
        loadCustomers();
      }
    } catch (error) {
      showToast(error.message || "Failed to delete customer.", "failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleActivateConfirmed = async () => {
    if (!customerToActivate?.unid) return;

    setIsActivating(true);
    try {
      await activateFMSCustomer(customerToActivate.unid);
      setCustomerToActivate(null);
      showToast("Customer activated successfully.", "success");

      if (isEditing) {
        loadCustomer({ background: true });
      } else {
        loadCustomers();
      }
    } catch (error) {
      showToast(error.message || "Failed to activate customer.", "failed");
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <>
      {isListView ? (
        <CustomerListView
          currentPage={currentPage}
          customers={customers}
          isLoading={isListLoading}
          onActivate={(target) => setCustomerToActivate(target)}
          onCreate={() => navigate("/customer-management/new")}
          onDelete={(target) => setCustomerToDelete(target)}
          onEdit={(unid) => navigate(`/customer-management/${unid}`)}
          onPageChange={setCurrentPage}
          pageCount={pageCount}
          totalCount={totalCount}
        />
      ) : (
        <CustomerEditorView
          customer={customer}
          formState={formState}
          initialFormState={initialFormState}
          isActivating={isActivating}
          isCreating={isCreating}
          isDeleting={isDeleting}
          isLoading={isDetailLoading}
          isSaving={isSaving}
          onActivate={() => {
            if (!customer) return;
            setCustomerToActivate(customer);
          }}
          onBack={() => {
            if (hasUnsavedChanges) {
              setIsDiscardChangesOpen(true);
              return;
            }
            navigate("/customer-management");
          }}
          onDelete={() => {
            if (!customer) return;
            setCustomerToDelete(customer);
          }}
          onNotify={showToast}
          onSave={handleSave}
          setFormState={setFormState}
        />
      )}

      {customerToDelete && (
        <ConfirmModal
          title="Delete Customer"
          subText="This action cannot be undone."
          confirmMessage={`${customerToDelete.name} (${customerToDelete.primaryContact}) will be permanently deleted.`}
          cancelText="Cancel"
          submitText={isDeleting ? "Deleting..." : "Delete Customer"}
          disableCancel={isDeleting}
          disableSubmit={isDeleting}
          onCancel={() => {
            if (isDeleting) return;
            setCustomerToDelete(null);
          }}
          onSubmit={handleDeleteConfirmed}
        />
      )}

      {isDiscardChangesOpen && (
        <ConfirmModal
          title="Discard Changes"
          subText="This customer has unsaved changes."
          confirmMessage="Leaving now will discard your unsaved changes."
          cancelText="Keep Editing"
          submitText="Discard Changes"
          onCancel={() => setIsDiscardChangesOpen(false)}
          onSubmit={() => {
            setIsDiscardChangesOpen(false);
            navigate("/customer-management");
          }}
        />
      )}

      {customerToActivate && (
        <ConfirmModal
          title="Activate Customer"
          subText="The customer will no longer be inactive."
          confirmMessage={`${customerToActivate.name} will be activated.`}
          cancelText="Cancel"
          submitText={isActivating ? "Activating..." : "Activate Customer"}
          disableCancel={isActivating}
          disableSubmit={isActivating}
          onCancel={() => {
            if (isActivating) return;
            setCustomerToActivate(null);
          }}
          onSubmit={handleActivateConfirmed}
        />
      )}

      <ToastMessage
        key={toast.id}
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={() =>
          setToast((current) => ({
            ...current,
            show: false,
          }))
        }
      />
    </>
  );
};

export default CustomerManagement;
